import { useEffect, useState } from "react";
import { Star, Loader2, RefreshCw, EyeOff, Ban, Eye, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { formatReviewDate, REVIEW_STATUS_LABELS, REVIEW_STATUS_STYLES } from "@/components/review/reviewMeta";

const FILTERS = [
  { value: "all", label: "Semua" },
  { value: "published", label: "Ditampilkan" },
  { value: "hidden", label: "Disembunyikan" },
  { value: "rejected", label: "Ditolak" },
];

export default function AdminReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all");
  const [acting, setActing] = useState(null);

  const load = (status = filter) => {
    setLoading(true);
    setError(false);
    const qs = status && status !== "all" ? `?status=${status}` : "";
    apiClient
      .get(`/admin/reviews${qs}`)
      .then((r) => setReviews(r.data || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  const moderate = async (id, action) => {
    setActing(id + action);
    try {
      await apiClient.post(`/admin/reviews/${id}/moderate`, { action });
      toast.success("Status ulasan diperbarui.");
      load(filter);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setActing(null);
    }
  };

  return (
    <AdminLayout testid="admin-reviews-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Moderasi Ulasan</h1>
          <p className="mt-2 text-sm text-muted-foreground">Kelola ulasan penghuni. Ulasan yang disembunyikan/ditolak tidak dihitung ke rating.</p>
        </div>
        <button onClick={() => load(filter)} data-testid="admin-reviews-refresh"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-primary border border-border hover:bg-secondary/60 transition-colors">
          <RefreshCw size={14} /> Muat Ulang
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="admin-reviews-filters">
        {FILTERS.map((f) => (
          <button key={f.value} onClick={() => setFilter(f.value)}
            data-testid={`admin-reviews-filter-${f.value}`}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              filter === f.value ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary/60"
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-10 grid place-items-center py-16"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : error ? (
        <div className="mt-8 rounded-2xl border border-destructive/30 bg-destructive/10 p-8 text-center" data-testid="admin-reviews-error">
          <p className="text-sm text-destructive">Gagal memuat ulasan.</p>
          <button onClick={() => load(filter)} className="mt-3 text-sm font-semibold text-primary hover:underline">Coba lagi</button>
        </div>
      ) : reviews.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-secondary/30 p-12 text-center" data-testid="admin-reviews-empty">
          <span className="grid place-items-center w-14 h-14 rounded-2xl bg-secondary text-primary mx-auto mb-4"><Star size={26} /></span>
          <h3 className="font-heading text-lg font-medium text-foreground">Belum ada ulasan.</h3>
        </div>
      ) : (
        <div className="mt-6 space-y-4" data-testid="admin-reviews-list">
          {reviews.map((rev, i) => (
            <div key={rev.id} data-testid={`admin-review-${i}`} className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <h3 className="font-heading text-base font-semibold truncate">{rev.property_name || "Hunian"}</h3>
                  <p className="text-sm text-muted-foreground">{rev.customer_name || "Pengguna AVASA"} · {formatReviewDate(rev.created_at)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${REVIEW_STATUS_STYLES[rev.status] || ""}`} data-testid={`admin-review-status-${i}`}>
                    {REVIEW_STATUS_LABELS[rev.status] || rev.status}
                  </span>
                  <span className="inline-flex items-center gap-1 text-sm font-bold text-amber-500">
                    <Star size={15} fill="currentColor" /> {rev.overall_rating}
                  </span>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>Kebersihan: <b className="text-foreground">{rev.cleanliness_rating}</b></span>
                <span>Lokasi: <b className="text-foreground">{rev.location_rating}</b></span>
                <span>Fasilitas: <b className="text-foreground">{rev.facility_rating}</b></span>
                <span>Owner: <b className="text-foreground">{rev.owner_rating}</b></span>
              </div>
              {rev.comment && (
                <p className="mt-3 text-sm text-foreground/90 leading-relaxed flex gap-2">
                  <MessageSquareText size={15} className="shrink-0 mt-0.5 text-muted-foreground" /> {rev.comment}
                </p>
              )}
              <div className="mt-4 flex flex-wrap gap-2 justify-end">
                {rev.status !== "published" && (
                  <button onClick={() => moderate(rev.id, "publish")} disabled={acting === rev.id + "publish"}
                    data-testid={`admin-review-publish-${i}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-emerald-700 border border-emerald-200 hover:bg-emerald-50 transition-colors disabled:opacity-60">
                    {acting === rev.id + "publish" ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Tampilkan
                  </button>
                )}
                {rev.status !== "hidden" && (
                  <button onClick={() => moderate(rev.id, "hide")} disabled={acting === rev.id + "hide"}
                    data-testid={`admin-review-hide-${i}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-amber-700 border border-amber-200 hover:bg-amber-50 transition-colors disabled:opacity-60">
                    {acting === rev.id + "hide" ? <Loader2 size={14} className="animate-spin" /> : <EyeOff size={14} />} Sembunyikan
                  </button>
                )}
                {rev.status !== "rejected" && (
                  <button onClick={() => moderate(rev.id, "reject")} disabled={acting === rev.id + "reject"}
                    data-testid={`admin-review-reject-${i}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-rose-600 border border-rose-200 hover:bg-rose-50 transition-colors disabled:opacity-60">
                    {acting === rev.id + "reject" ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />} Tolak
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
